import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Material } from '@texas/api/endpoints/metadata/materialsApi';
import {
  MaterialComplianceDocument,
  materialComplianceDocumentsApi,
} from '@texas/api/endpoints/metadata/materialComplianceDocumentsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useActiveSession } from '@texas/hooks/useSession';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { AssignMaterialComplianceDocument } from './AssignMaterialComplianceDocument';
import { materialComplianceDocumentColumns } from './columns';
import { materialComplianceDocumentEvents } from './events';

interface Props {
  material: Material;
  onClose: () => void;
}

export function MaterialComplianceDocuments({ material, onClose }: Props) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit =
    !material.archived && hasRole(currentUser.role, [Role.SystemAdministrator]);
  const { data, refetch, loading, error } = useApiResource(
    materialComplianceDocumentsApi.get,
  );
  const assignDisclosure = useDisclosure();
  const removeDisclosure = useValueDisclosure<MaterialComplianceDocument>();
  const documents = data ?? [];
  const pending = loading || (data === null && error === null);
  const columns = useMemo(
    () =>
      materialComplianceDocumentColumns(t, canEdit, removeDisclosure.onOpen),
    [t, canEdit, removeDisclosure.onOpen],
  );

  useEffect(() => {
    refetch(material.id);
  }, [material.id, refetch]);

  useEffect(() => {
    return materialComplianceDocumentEvents.changed.subscribe((materialId) => {
      if (materialId === material.id) refetch(material.id);
    });
  }, [material.id, refetch]);

  return (
    <>
      <Modal
        variant="entityDirectory"
        size="5xl"
        isOpen={true}
        onClose={onClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('complianceDocumentDefinitions.title')}
            <Text
              variant="entity-directory-description"
              textTransform="none"
              mt={1}
            >
              {material.code} · {material.name}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                <Text variant="entity-directory-description">
                  {t('materialComplianceDocuments.description')}
                </Text>
                {canEdit && (
                  <Button
                    variant="entityDirectoryPrimary"
                    isDisabled={pending || !!error}
                    onClick={assignDisclosure.onOpen}
                  >
                    {t('customerComplianceDocuments.assignFromConfigs')}
                  </Button>
                )}
              </Flex>
              {pending ? (
                <SpinnerWithLabel label={t('general.loadingData')} />
              ) : error ? (
                <ErrorDetails error={error} />
              ) : documents.length === 0 ? (
                <EntityDirectoryEmptyState>
                  {t('materialComplianceDocuments.empty')}
                </EntityDirectoryEmptyState>
              ) : (
                <DataTableContainer
                  error={null}
                  showColumnToggle={false}
                  datatable={{
                    columns,
                    data: documents,
                    variant: 'entityDirectoryCompact',
                    rowProps: {
                      getRowId: (document) =>
                        document.complianceDocumentDefinitionId.toString(),
                    },
                  }}
                />
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="entityDirectorySecondary" onClick={onClose}>
              {t('general.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {canEdit && assignDisclosure.isOpen && (
        <AssignMaterialComplianceDocument
          material={material}
          documents={documents}
          onClose={assignDisclosure.onClose}
        />
      )}
      {canEdit && removeDisclosure.value && (
        <VerifyDialogWithRequest
          headerTitle={t('customerComplianceDocuments.removeTitle')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.remove')}
          primaryRequest={materialComplianceDocumentsApi.remove}
          args={[
            material.id,
            removeDisclosure.value.complianceDocumentDefinitionId,
          ]}
          isOpen={removeDisclosure.isOpen}
          onClose={removeDisclosure.onClose}
          onPerformed={() =>
            materialComplianceDocumentEvents.changed.dispatch(material.id)
          }
          onSuccessTitle={t('general.removed')}
        >
          {t('customerComplianceDocuments.removeConfirmation', {
            definition: removeDisclosure.value.name,
            name: material.name,
          })}
        </VerifyDialogWithRequest>
      )}
    </>
  );
}
